import { useMemo } from 'react';

import { Box } from 'components/Box/Box';
import { Icon } from 'components/Icon/Icon';
import { Tabs } from 'components/Tabs/Tabs';
import { createTab } from 'components/Tabs/createTab';
import { useQueryParamState } from 'hooks/useQueryParamState';
import type { ApiOrg } from 'modules/listing/api/org/types/ApiOrg';
import { colors, spacing } from 'theme/theme';

import { ViewOrgPageListingsTabsTabLabel } from './ViewOrgPageListingsTabsLabel';
import { ViewOrgPageTruncatedListings } from './components/ViewOrgPageTruncatedListings';
import type { ViewOrgPageListingTab } from './types/ViewOrgPageListingTab';
import { isViewOrgPageListingTab } from './types/ViewOrgPageListingTab';

type Props = {
  org: ApiOrg;
  defaultTab: ViewOrgPageListingTab;
};

export function ViewOrgPageListingsTabs({ org, defaultTab }: Props) {
  const [queryTabSlug, setTabSlug] = useQueryParamState({
    name: 'tab',
  });

  const tabSlug = isViewOrgPageListingTab(queryTabSlug)
    ? queryTabSlug
    : defaultTab;

  const tabs = useMemo(
    () =>
      [
        org.jobs.length > 0 &&
          createTab({
            prefix: 'org-listings',
            value: 'JOB',
            label: (
              <ViewOrgPageListingsTabsTabLabel
                $active={tabSlug === 'JOB'}
                count={org.jobs.length}
                icon={<Icon name="briefcase" colorName="white" size={24} />}
              >
                {getText('Jobs')}
              </ViewOrgPageListingsTabsTabLabel>
            ),
          }),
        org.internships.length > 0 &&
          createTab({
            prefix: 'org-listings',
            value: 'INTERNSHIP',
            label: (
              <ViewOrgPageListingsTabsTabLabel
                $active={tabSlug === 'INTERNSHIP'}
                count={org.internships.length}
                icon={<Icon name="sign-path" colorName="white" size={24} />}
              >
                {getText('Internships')}
              </ViewOrgPageListingsTabsTabLabel>
            ),
          }),
        org.volops.length > 0 &&
          createTab({
            prefix: 'org-listings',
            value: 'VOLOP',
            label: (
              <ViewOrgPageListingsTabsTabLabel
                $active={tabSlug === 'VOLOP'}
                count={org.volops.length}
                icon={<Icon name="hand" colorName="white" size={24} />}
              >
                {getText('Volunteering')}
              </ViewOrgPageListingsTabsTabLabel>
            ),
          }),
        org.events.length > 0 &&
          createTab({
            prefix: 'org-listings',
            value: 'EVENT',
            label: (
              <ViewOrgPageListingsTabsTabLabel
                $active={tabSlug === 'EVENT'}
                count={org.events.length}
                icon={<Icon name="star-calendar" colorName="white" size={24} />}
              >
                {getText('Events')}
              </ViewOrgPageListingsTabsTabLabel>
            ),
          }),
      ].filter(Boolean),
    [org, tabSlug],
  );

  const currentListings = useMemo(() => {
    switch (tabSlug) {
      case 'JOB':
        return org.jobs;
      case 'INTERNSHIP':
        return org.internships;
      case 'VOLOP':
        return org.volops;
      case 'EVENT':
        return org.events;
    }
  }, [org, tabSlug]);

  return (
    <>
      <Box borderBottom={`1px solid ${colors.calloutGrey}`}>
        <Tabs
          tabs={tabs}
          value={tabSlug}
          onSelectTab={(value) => setTabSlug(value)}
          variant="compact-marker"
          layout="horizontal"
        />
      </Box>

      <Box mt={spacing[40]}>
        <ViewOrgPageTruncatedListings
          listingType={tabSlug}
          listings={currentListings}
        />
      </Box>
    </>
  );
}
